import type {
  CartItem,
  CheckoutPaymentInput,
  CheckoutResult,
  CustomerDTO,
} from '@horology/shared-types';
import { api } from '../../lib/api';

export interface CheckoutBody {
  customerId?: number;
  warehouseId: number;
  serviceOrderId?: number;
  notes?: string;
  items: Array<{
    productId: number;
    quantity: number;
    unitPriceOverride?: number;
    discount?: number;
  }>;
  payments: CheckoutPaymentInput[];
}

export interface SettleableOrder {
  id: number;
  folio: string;
  state: string;
  finalValue: string | null;
  estimatedValue: string;
  customer: { firstName: string; lastName: string; idDocument: string; phone: string };
  items: Array<{
    id: number;
    productId: number;
    description: string | null;
    quantity: string;
    unitPrice: string;
    taxRate: string;
    isLabor: boolean;
    product: { id: number; sku: string; name: string; isService: boolean };
  }>;
}

export async function listSettleableOrders(q?: string): Promise<SettleableOrder[]> {
  const { data } = await api.get<SettleableOrder[]>('/api/pos/settleable-orders', {
    params: q ? { q } : undefined,
  });
  return data;
}

export async function issueInvoiceToDian(invoiceId: number): Promise<{ cufe: string; status: string }> {
  const { data } = await api.post<{ cufe: string; status: string }>(
    `/api/dian/invoices/${invoiceId}/issue`,
  );
  return data;
}

export async function checkout(body: CheckoutBody): Promise<CheckoutResult> {
  const { data } = await api.post<CheckoutResult>('/api/pos/checkout', body);
  return data;
}

export async function searchCustomers(q: string): Promise<CustomerDTO[]> {
  const { data } = await api.get<{ data: CustomerDTO[] }>('/api/customers', {
    params: { q, limit: 20 },
  });
  return data.data;
}

export async function createCustomer(body: {
  idDocument: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}): Promise<CustomerDTO> {
  const { data } = await api.post<CustomerDTO>('/api/customers', body);
  return data;
}

export function cartTotals(items: CartItem[]): {
  subtotal: number;
  taxTotal: number;
  total: number;
} {
  let subtotal = 0;
  let taxTotal = 0;
  for (const i of items) {
    const lineGross = i.unitPrice * i.quantity - i.discount;
    const lineNet = Math.round((lineGross / (1 + i.taxRate / 100)) * 100) / 100;
    const lineTax = Math.round((lineGross - lineNet) * 100) / 100;
    subtotal += lineNet;
    taxTotal += lineTax;
  }
  subtotal = Math.round(subtotal * 100) / 100;
  taxTotal = Math.round(taxTotal * 100) / 100;
  return { subtotal, taxTotal, total: Math.round((subtotal + taxTotal) * 100) / 100 };
}
